export type AnalyticsOutcome = 'success' | 'error';
export type AnalyticsErrorCode =
  | 'none'
  | 'auth_failed'
  | 'invalid_state'
  | 'missing_code'
  | 'permission_denied'
  | 'validation'
  | 'network'
  | 'timeout'
  | 'not_found'
  | 'billing_unavailable'
  | 'unknown';
export type AnalyticsSurface =
  | 'marketing'
  | 'pricing'
  | 'server_list'
  | 'server_dashboard'
  | 'feature_page'
  | 'support'
  | 'profile';
export type AnalyticsPlan = 'free' | 'professional' | 'premium' | 'other';
export type AnalyticsFeature =
  | 'create_player'
  | 'switch_worlds'
  | 'unpause_world'
  | 'get_current_world'
  | 'discord_integration'
  | 'other';
export type PageType =
  'marketing' | 'dashboard' | 'auth' | 'account' | 'legal' | 'support' | 'admin' | 'other';

export interface AnalyticsEvents {
  page_view: {
    page_path: string;
    page_type: PageType;
    page_location: string;
    page_referrer: string;
  };
  login_start: {
    method: 'discord';
    surface: 'signin';
  };
  login_outcome: {
    method: 'discord';
    outcome: AnalyticsOutcome;
    error_code: AnalyticsErrorCode;
  };
  server_list_view: {
    count_bucket: '0' | '1' | '2-5' | '6-10' | '11+';
  };
  server_select: {
    surface: 'server_list' | 'sidebar';
  };
  foundry_config_save: {
    field: 'foundry_url' | 'gm_password' | 'admin_password';
    outcome: AnalyticsOutcome;
    error_code: AnalyticsErrorCode;
  };
  feature_config_open: {
    feature: AnalyticsFeature;
    surface: 'feature_page';
  };
  feature_config_save: {
    feature: AnalyticsFeature;
    setting: 'enabled';
    enabled: boolean;
    outcome: AnalyticsOutcome;
    error_code: AnalyticsErrorCode;
  };
  begin_checkout: {
    plan: AnalyticsPlan;
    surface: 'server_dashboard';
  };
  billing_portal_open: {
    outcome: AnalyticsOutcome;
  };
  invite_cta: {
    surface: AnalyticsSurface;
  };
  support_submit: {
    category: 'general' | 'technical' | 'billing' | 'feature' | 'bug' | 'feedback' | 'other';
    outcome: AnalyticsOutcome;
    error_code: AnalyticsErrorCode;
  };
  account_delete: {
    outcome: AnalyticsOutcome;
    error_code: AnalyticsErrorCode;
  };
  web_vital: {
    metric: 'CLS' | 'INP' | 'FCP' | 'LCP' | 'TTFB';
    rating: 'good' | 'needs-improvement' | 'poor';
    value: number;
  };
}

export type AnalyticsEventName = keyof AnalyticsEvents;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __alpha5GaInitialized?: boolean;
  }
}

const EVENT_FIELDS: Record<AnalyticsEventName, ReadonlySet<string>> = {
  page_view: new Set(['page_path', 'page_type', 'page_location', 'page_referrer']),
  login_start: new Set(['method', 'surface']),
  login_outcome: new Set(['method', 'outcome', 'error_code']),
  server_list_view: new Set(['count_bucket']),
  server_select: new Set(['surface']),
  foundry_config_save: new Set(['field', 'outcome', 'error_code']),
  feature_config_open: new Set(['feature', 'surface']),
  feature_config_save: new Set(['feature', 'setting', 'enabled', 'outcome', 'error_code']),
  begin_checkout: new Set(['plan', 'surface']),
  billing_portal_open: new Set(['outcome']),
  invite_cta: new Set(['surface']),
  support_submit: new Set(['category', 'outcome', 'error_code']),
  account_delete: new Set(['outcome', 'error_code']),
  web_vital: new Set(['metric', 'rating', 'value']),
};

export function trackEvent<K extends AnalyticsEventName>(
  name: K,
  params: AnalyticsEvents[K],
): void {
  if (typeof window === 'undefined' || !window.gtag) return;

  const allowed = EVENT_FIELDS[name];
  const safeParams = Object.fromEntries(Object.entries(params).filter(([key]) => allowed.has(key)));
  window.gtag('event', name, safeParams);
}

export function toSafePagePath(input: string): string {
  let pathname = input;
  try {
    if (/^https?:\/\//i.test(input)) pathname = new URL(input).pathname;
  } catch {
    pathname = '/';
  }

  pathname = pathname.split(/[?#]/, 1)[0] || '/';
  return pathname
    .replace(/(\/[a-z]{2}\/(?:admin\/)?servers)\/[^/]+/i, '$1/:server')
    .replace(/\/features\/[^/]+/i, '/features/:feature');
}

export function toPageType(pathname: string): PageType {
  const safePath = toSafePagePath(pathname);
  if (/\/auth\//.test(safePath)) return 'auth';
  if (/\/servers(?:\/|$)/.test(safePath) || /\/user\/home/.test(safePath)) return 'dashboard';
  if (/\/user\//.test(safePath)) return 'account';
  if (/\/(?:privacy-policy|terms-of-service)(?:\/|$)/.test(safePath)) return 'legal';
  if (/\/support(?:\/|$)/.test(safePath)) return 'support';
  if (/\/admin(?:\/|$)/.test(safePath)) return 'admin';
  if (/\/(?:home|pricing|faq|how-it-works|setup)(?:\/|$)/.test(safePath) || safePath === '/') {
    return 'marketing';
  }
  return 'other';
}

export function toSafePageLocation(input: string): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  const pagePath = toSafePagePath(input);
  return origin ? `${origin}${pagePath}` : pagePath;
}

export function toSafePageReferrer(
  referrer = typeof document !== 'undefined' ? document.referrer : '',
): string {
  if (!referrer) return '';

  try {
    const url = new URL(referrer);
    const currentOrigin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    if (currentOrigin && url.origin === currentOrigin) {
      return `${url.origin}${toSafePagePath(url.pathname)}`;
    }
    return url.origin;
  } catch {
    return '';
  }
}

export function trackPageView(pathname: string): void {
  const pagePath = toSafePagePath(pathname);
  trackEvent('page_view', {
    page_path: pagePath,
    page_type: toPageType(pagePath),
    page_location: toSafePageLocation(pathname),
    page_referrer: toSafePageReferrer(),
  });
}

export function bucketServerCount(
  count: number,
): AnalyticsEvents['server_list_view']['count_bucket'] {
  if (count <= 0) return '0';
  if (count === 1) return '1';
  if (count <= 5) return '2-5';
  if (count <= 10) return '6-10';
  return '11+';
}

export function toSafeFeature(feature: string): AnalyticsFeature {
  const normalized = feature.replace(/[-\s]/g, '').toLowerCase();
  const features: Record<string, AnalyticsFeature> = {
    createplayer: 'create_player',
    switchworlds: 'switch_worlds',
    switchworld: 'switch_worlds',
    unpauseworld: 'unpause_world',
    getcurrentworld: 'get_current_world',
    discordintegration: 'discord_integration',
  };
  return features[normalized] || 'other';
}

export function toSafePlan(plan: string): AnalyticsPlan {
  const normalized = plan.toLowerCase();
  if (normalized === 'free' || normalized === 'professional' || normalized === 'premium') {
    return normalized;
  }
  return 'other';
}

export function toErrorCode(error: unknown): AnalyticsErrorCode {
  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  if (/timed? out|timeout/.test(message)) return 'timeout';
  if (/network|fetch|connection|econn/.test(message)) return 'network';
  if (/permission|authorized|forbidden/.test(message)) return 'permission_denied';
  if (/valid|required|invalid/.test(message)) return 'validation';
  if (/not found|missing/.test(message)) return 'not_found';
  if (/billing|checkout|portal/.test(message)) return 'billing_unavailable';
  return 'unknown';
}

export function toSupportCategory(category: string): AnalyticsEvents['support_submit']['category'] {
  const allowed = new Set([
    'general',
    'technical',
    'billing',
    'feature',
    'bug',
    'feedback',
    'other',
  ]);
  return allowed.has(category)
    ? (category as AnalyticsEvents['support_submit']['category'])
    : 'other';
}
